import { FaLinkedin, FaGithub, FaEnvelope, FaPaperPlane, FaBriefcase, FaCertificate } from "react-icons/fa";
import { FaArrowUpRightFromSquare } from "react-icons/fa6";
import { useLocale } from "../i18n/LocaleContext";

const SOCIALS = [
  { icon: FaLinkedin, href: "https://linkedin.com/in/guidoenr", label: "LinkedIn" },
  { icon: FaGithub,   href: "https://github.com/guidoenr",      label: "GitHub" },
  { icon: FaEnvelope, href: "mailto:guidoenr4@gmail.com",       label: "Email" },
];

const FLAGS = [
  { code: "us", emoji: "\u{1F1FA}\u{1F1F8}", label: "United States", locale: "en" },
  { code: "uk", emoji: "\u{1F1EC}\u{1F1E7}", label: "United Kingdom", locale: "en" },
  { code: "mx", emoji: "\u{1F1F2}\u{1F1FD}", label: "Mexico", locale: "es" },
  { code: "ar", emoji: "\u{1F1E6}\u{1F1F7}", label: "Argentina", locale: "es" },
];

export function Hero() {
  const { setLocale, t } = useLocale();

  return (
    <section className="relative w-full">
      <aside className="hidden md:flex flex-col gap-5 fixed left-6 top-1/2 -translate-y-1/2 z-20">
        {SOCIALS.map(({ icon: Icon, href, label }) => (
          <a
            key={label}
            href={href}
            target={href.startsWith("mailto:") ? undefined : "_blank"}
            rel="noopener noreferrer"
            aria-label={label}
            className="text-zinc-500 dark:text-zinc-400 hover:text-zinc-800 dark:hover:text-zinc-100 hover:-translate-y-0.5 transition-all duration-200"
          >
            <Icon className="w-5 h-5" />
          </a>
        ))}
        <div className="w-px h-12 bg-zinc-300 dark:bg-zinc-700 mx-auto mt-2" />
      </aside>

      <div className="max-w-6xl mx-auto px-6 md:px-16 pt-20 md:pt-28 pb-12">
        <div className="grid md:grid-cols-[1.1fr_0.9fr] gap-10 md:gap-16 items-center">
          <div className="flex flex-col gap-6">
            <div className="inline-flex items-center gap-4 self-start">
              <span className="text-xs font-semibold tracking-[0.18em] text-zinc-400 dark:text-zinc-500">
                {t("hero.workedFor")}
              </span>
              <span className="flex items-center gap-1.5 text-1xl leading-none">
                {FLAGS.map((f) => (
                  <button
                    key={f.code}
                    type="button"
                    title={f.label}
                    aria-label={f.label}
                    onClick={() => setLocale(f.locale)}
                    className="inline-block cursor-pointer transition-transform hover:scale-110 active:scale-95"
                  >
                    {f.emoji}
                  </button>
                ))}
              </span>
            </div>

            <h1 className="text-5xl md:text-7xl font-black tracking-tight text-zinc-900 dark:text-zinc-100 leading-[1.05]">
              Guido Enrique
            </h1>

            <div className="flex items-center gap-4 -mt-2">
              <span className="block w-12 h-px bg-zinc-400 dark:bg-zinc-600" />
              <p className="text-sm md:text-base text-zinc-600 dark:text-zinc-400 font-light tracking-wide">
                {t("hero.role")}
              </p>
            </div>

            <p className="text-base md:text-md text-zinc-700 dark:text-zinc-300 leading-relaxed max-w-xl mb-7">
              {t("hero.bio")} <br /> {t("hero.bio2")} <br /> {t("hero.bio3")}
            </p>

            <div className="flex flex-col gap-3 -mt-1">
              <a
                href="https://drive.google.com/file/d/1UEowTMRtQp9FbmE2NSWEkI-d0aYQKvBF/view?usp=sharing"
                target="_blank"
                rel="noopener noreferrer"
                title="View degree certificate"
                className="group inline-flex items-center gap-3 self-start max-w-xl"
              >
                <span className="flex items-center justify-center w-12 h-8 shrink-0">
                  <img
                    src="/resources/utn-logo.png"
                    alt="UTN"
                    className="max-h-8 max-w-full w-auto opacity-70 group-hover:opacity-100 transition-opacity duration-200"
                    draggable="false"
                  />
                </span>
                <span className="flex flex-col leading-tight">
                  <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400 group-hover:text-zinc-800 dark:group-hover:text-zinc-100 transition-colors">
                    {t("hero.graduated")}
                  </span>
                  <span className="text-sm text-zinc-700 dark:text-zinc-300 font-medium group-hover:text-zinc-900 dark:group-hover:text-zinc-100 transition-colors inline-flex items-center gap-1.5">
                    {t("hero.degree")}
                    <FaArrowUpRightFromSquare className="w-2.5 h-2.5 text-zinc-400 dark:text-zinc-500 group-hover:text-zinc-700 dark:group-hover:text-zinc-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
                  </span>
                </span>
              </a>

              <a
                href="#experience"
                title="Jump to experience"
                className="group inline-flex items-center gap-3 self-start max-w-xl"
              >
                <span className="flex items-center justify-center w-12 h-8 shrink-0">
                  <FaBriefcase className="w-5 h-5 text-zinc-400 dark:text-zinc-500 group-hover:text-zinc-700 dark:group-hover:text-zinc-300 group-hover:scale-110 transition-all duration-200" />
                </span>
                <span className="flex flex-col leading-tight">
                  <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400 group-hover:text-zinc-800 dark:group-hover:text-zinc-100 transition-colors">
                    {t("hero.experience")}
                  </span>
                  <span className="text-sm text-zinc-700 dark:text-zinc-300 font-medium group-hover:text-zinc-900 dark:group-hover:text-zinc-100 transition-colors inline-flex items-center gap-1.5">
                    {t("hero.experienceLine")}
                    <FaArrowUpRightFromSquare className="w-2.5 h-2.5 text-zinc-400 dark:text-zinc-500 group-hover:text-zinc-700 dark:group-hover:text-zinc-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
                  </span>
                </span>
              </a>

              <a
                href="https://linkedin.com/in/guidoenr/details/certifications/"
                target="_blank"
                rel="noopener noreferrer"
                title="View certifications on LinkedIn"
                className="group inline-flex items-center gap-3 self-start max-w-xl"
              >
                <span className="flex items-center justify-center w-12 h-8 shrink-0">
                  <FaCertificate className="w-5 h-5 text-zinc-400 dark:text-zinc-500 group-hover:text-zinc-700 dark:group-hover:text-zinc-300 group-hover:scale-110 transition-all duration-200" />
                </span>
                <span className="flex flex-col leading-tight">
                  <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400 group-hover:text-zinc-800 dark:group-hover:text-zinc-100 transition-colors">
                    {t("hero.alwaysLearning")}
                  </span>
                  <span className="text-sm text-zinc-700 dark:text-zinc-300 font-medium group-hover:text-zinc-900 dark:group-hover:text-zinc-100 transition-colors inline-flex items-center gap-1.5">
                    {t("hero.learningLine")}
                    <FaArrowUpRightFromSquare className="w-2.5 h-2.5 text-zinc-400 dark:text-zinc-500 group-hover:text-zinc-700 dark:group-hover:text-zinc-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
                  </span>
                </span>
              </a>
            </div>

            <div className="flex flex-wrap gap-3 mt-2 translate-x-[15px]">
              <a
                href="mailto:guidoenr4@gmail.com"
                className="group inline-flex items-center gap-3 px-6 py-3 rounded-full bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 font-medium hover:bg-zinc-800 dark:hover:bg-white transition-all duration-200 shadow-lg shadow-zinc-900/10 hover:shadow-zinc-900/25 dark:shadow-black/30 hover:-translate-y-0.5"
              >
                {t("hero.contactMe")}
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-white/15 dark:bg-zinc-900/15 group-hover:bg-white/25 dark:group-hover:bg-zinc-900/25 transition-colors">
                  <FaPaperPlane className="w-3 h-3 -rotate-12 group-hover:rotate-0 transition-transform" />
                </span>
              </a>
            </div>

            <div className="flex md:hidden gap-5 mt-2">
              {SOCIALS.map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target={href.startsWith("mailto:") ? undefined : "_blank"}
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="text-zinc-500 dark:text-zinc-400 hover:text-zinc-800 dark:hover:text-zinc-100 transition-colors"
                >
                  <Icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          <div className="relative flex justify-center md:justify-end">
            <div className="relative">
              <div
                aria-hidden="true"
                className="absolute inset-0 bg-zinc-400/30 dark:bg-indigo-500/20 blur-3xl scale-90"
              />
              <div className="relative overflow-hidden rounded-full w-72 h-72 sm:w-80 sm:h-80 md:w-96 md:h-96 shadow-2xl shadow-zinc-900/15 dark:shadow-black/40 ring-1 ring-white/70 dark:ring-white/10 bg-white dark:bg-zinc-800">
                <img
                  src="/resources/guidoti-profile.png"
                  alt="Guido Enrique"
                  className="w-full h-full object-cover"
                  draggable="false"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
